import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const BLACK = '#0a0a0a';
const NAVY  = '#1e3a4a';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

export default function AgreementsPanel() {
  const [agreements, setAgreements] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [deleting,   setDeleting]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.getAgreements();
      setAgreements(rows);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDownload(id, name) {
    try {
      const token = localStorage.getItem('acts_token');
      const res = await fetch(`/api/contractor-agreement/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `agreement-${name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error downloading PDF: ' + err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this agreement permanently?')) return;
    setDeleting(id);
    try {
      await api.deleteAgreement(id);
      load();
    } catch (err) {
      alert('Error: ' + (err.message || 'Failed to delete'));
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div style={{ padding: '16px 16px 80px', maxWidth: 900, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: BLACK, letterSpacing: 1 }}>CONTRACTOR AGREEMENTS</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
            Signed independent contractor agreements
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 0, padding: '4px 14px', fontWeight: 700, fontSize: 13 }}>
            {agreements.length} total
          </span>
          <button onClick={load} style={{ padding: '7px 14px', borderRadius: 0, border: '1px solid #e0e0e0', background: '#f9fafb', color: '#374151', fontSize: 12, cursor: 'pointer' }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Share link */}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 0, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>📋 Agreement Link:</span>
        <code style={{ fontSize: 12, color: '#374151', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 0, padding: '4px 10px', wordBreak: 'break-all' }}>
          {window.location.origin}/contractor-agreement
        </code>
        <button
          onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/contractor-agreement`)}
          style={{ padding: '5px 12px', borderRadius: 0, border: '1px solid #fde68a', background: '#fef3c7', color: '#92400e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          Copy Link
        </button>
      </div>

      {loading && <p style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading...</p>}

      {!loading && agreements.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 }}>
          No agreements signed yet.
        </div>
      )}

      {/* Agreement cards */}
      {agreements.map(a => (
        <div key={a.id} style={{
          background: '#fff', border: '1px solid #e0e0e0', borderRadius: 0,
          padding: '18px 20px', marginBottom: 14,
          borderLeft: '4px solid #16a34a',
        }}>
          {/* Top row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 16, color: BLACK }}>
                {a.first_name} {a.last_name}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                Signed {fmtDate(a.signed_at)}
              </div>
            </div>
            <span style={{ background: '#d1fae5', color: '#065f46', padding: '3px 10px', borderRadius: 0, fontSize: 11, fontWeight: 700 }}>
              ✓ Signed
            </span>
          </div>

          {/* Details grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
            {[
              ['Date of Birth', a.dob],
              ['Email',         a.email],
              ['Phone',         a.phone],
              ['ABN',           a.abn],
              ['BSB',           a.bsb],
              ['Account No.',   a.account_number],
            ].map(([label, val]) => (
              <div key={label} style={{ background: '#f9fafb', borderRadius: 0, padding: '10px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: BLACK }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => handleDownload(a.id, `${a.first_name}-${a.last_name}`)}
              style={{
                flex: 1, minWidth: 120, padding: '10px', borderRadius: 0, border: 'none',
                background: BLACK, color: '#FFD000',
                fontWeight: 800, fontSize: 13, cursor: 'pointer',
              }}
            >
              ⬇ Download PDF
            </button>
            <button
              onClick={() => handleDelete(a.id)}
              disabled={deleting === a.id}
              style={{
                padding: '10px 18px', borderRadius: 0, border: '1px solid #fca5a5',
                background: '#fff1f1', color: '#dc2626',
                fontWeight: 700, fontSize: 13, cursor: deleting === a.id ? 'not-allowed' : 'pointer',
              }}
            >
              🗑 Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
